import { gql, type TypedDocumentNode } from '@apollo/client';
import { useNavigate } from 'react-router';

import {
  Rarity,
  So5LeaderboardGroupType,
} from '@sorare/core/src/__generated__/globalTypes';
import {
  ANY_SPORT_PLAY_BOARDS_ARENA_FIXTURE,
  ANY_SPORT_PLAY_BOARDS_ARENA_FIXTURE_COMPETITION,
  ANY_SPORT_PLAY_BOARDS_PRO,
} from '@sorare/core/src/constants/__generated__/routes.ts';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { isTypeFn } from '@sorare/core/src/lib/gql/index.ts';

import { BoardLayout } from '#src/components/boards/BoardLayout/index.tsx';
import { ArenaPickers } from '#src/components/pickers/ArenaPickers/index.tsx';
import { Mode } from '#src/components/pickers/ModePicker/index.tsx';
import { ProBoardHeaderWrapper } from '#src/components/pro/ProBoard/ProBoardHeaderWrapper/index.tsx';
import { useLastVisitedBoardView } from '#src/hooks/useLastVisitedBoardView.ts';
import { getFixtureQueryVariables } from '#src/lib/so5.ts';

import type {
  ArenaBoardQuery,
  ArenaBoardQueryVariables,
} from './__generated__/index.graphql.ts';

type Props = { fixture: string; competition: string };

const ARENA_BOARD_QUERY = gql`
  query ArenaBoardQuery(
    $type: So5State
    $slug: String
    $sport: Sport!
    $eventType: So5FixtureEvent = CLASSIC
    $groupType: So5LeaderboardGroupType
  ) {
    so5 {
      so5Fixture(
        type: $type
        slug: $slug
        sport: $sport
        eventType: $eventType
      ) {
        slug
        so5LeaderboardGroups(groupType: $groupType) {
          slug
          ... on So5LeaderboardGroup {
            slug
            ...ArenaPickers_so5LeaderboardGroup
          }
        }
      }
    }
  }
  ${ArenaPickers.fragments.so5LeaderboardGroup}
` as TypedDocumentNode<ArenaBoardQuery, ArenaBoardQueryVariables>;

export const ArenaBoard = ({ fixture, competition }: Props) => {
  const navigate = useNavigate();
  const { generateSportContextPath, sport } = useSportContext();
  const { lastVisitedView, updateBoardView } = useLastVisitedBoardView();

  const { data } = useQuery(ARENA_BOARD_QUERY, {
    variables: {
      ...getFixtureQueryVariables(fixture),
      sport,
      groupType: So5LeaderboardGroupType.ARENA,
    },
  });

  const selectedMode = lastVisitedView?.lastVisitedMode ?? Mode.ARENA_CASH;
  const selectedRarity =
    lastVisitedView?.lastVisitedArenaRarity ?? Rarity.limited;

  const groups = (data?.so5.so5Fixture?.so5LeaderboardGroups ?? []).filter(
    isTypeFn('So5LeaderboardGroup')
  );

  const handleModeSelect = (mode: Mode) => {
    updateBoardView({ lastVisitedMode: mode });
    navigate(
      generateSportContextPath(
        mode === Mode.PRO
          ? ANY_SPORT_PLAY_BOARDS_PRO
          : ANY_SPORT_PLAY_BOARDS_ARENA_FIXTURE,
        { params: { fixture } }
      )
    );
  };

  const handleCompetitionSelect = (slug: string) => {
    navigate(
      generateSportContextPath(
        ANY_SPORT_PLAY_BOARDS_ARENA_FIXTURE_COMPETITION,
        {
          params: { fixture, competition: slug },
        }
      )
    );
  };

  return (
    <BoardLayout
      header={<ProBoardHeaderWrapper />}
      bottomBar={
        <ArenaPickers
          selectedRarity={selectedRarity}
          onSelectRarity={rarity =>
            updateBoardView({ lastVisitedArenaRarity: rarity })
          }
          selectedMode={selectedMode}
          onSelectMode={handleModeSelect}
          competitionGroups={groups.map(g => ({
            ...g,
            so5Leaderboards: g.so5Leaderboards.filter(
              l => l.mainRarityType === selectedRarity
            ),
          }))}
          selectedLeaderboard={competition}
          onSelectLeaderboard={handleCompetitionSelect}
        />
      }
    />
  );
};
